import moment from 'moment';
import { MediaContent, assetsMediaContentList } from '@goldcast/api/content';
import { useState, useEffect } from 'react';
import useContentUpload from '@/hooks/useContentUpload';
import { ContentStatesEnum } from '@/Pages/VideoImportPage/constants';
import { currentUser } from '@/stores/user';

export default function useMediaContent() {
  const [mediaContent, setMediaContent] = useState<MediaContent[]>([]);
  const [showErrorState, setShowErrorState] = useState(false);
  const { contentUploadList } = useContentUpload();

  useEffect(() => {
    if (!mediaContent.length) {
      return;
    }

    const inProgressUploads = Object.values(contentUploadList).filter(
      content =>
        content.contentState === ContentStatesEnum.Uploading ||
        content.contentState === ContentStatesEnum.Processing ||
        content.contentState === ContentStatesEnum.Generating
    );

    if (!inProgressUploads.length) {
      return;
    }

    // Map the live client-side upload state to the optimistic card's batch_status. Only the raw
    // client->Filestack transfer is 'UPLOADING'; the moment the transfer finishes the client state
    // advances to Processing, so the card reads 'PROCESSING'. This keeps the Sessions thumbnail in
    // lockstep with the readiness popup instead of waiting for the (late) list refetch that swaps in
    // the real backend record — and lets the raw-upload disable in getSessionPreviewState release
    // exactly at Processing.
    const toBatchStatus = (contentState: ContentStatesEnum) =>
      contentState === ContentStatesEnum.Uploading ? 'UPLOADING' : 'PROCESSING';

    // New uploads with no card yet -> inject an optimistic placeholder.
    const newUploads = inProgressUploads.filter(content => !mediaContent.some(mc => mc.id === content.contentId));

    // Uploads whose optimistic card is already shown but whose state has advanced -> resync its
    // batch_status. Guard on the __isOptimistic marker so we never clobber the real backend record
    // once a refetch has replaced the placeholder.
    const staleOptimisticUploads = inProgressUploads.filter(content =>
      mediaContent.some(
        mc =>
          mc.id === content.contentId &&
          (mc as any).__isOptimistic &&
          mc.batch_status !== toBatchStatus(content.contentState)
      )
    );

    if (!newUploads.length && !staleOptimisticUploads.length) {
      return;
    }

    const contentUpload = newUploads.map(content => {
      return {
        id: content.contentId,
        project_id: content.projectId,
        media_source_type: 'UPLOAD',
        batch_status: toBatchStatus(content.contentState),
        __isOptimistic: true,
        organization: currentUser.getSnapshot()?.organization as string,
        av_type: content.av_type,
        recording_date: moment().format('YYYY-MM-DD'),
        title: content.title,
        broadcast_type: 'SIMULIVE',
        reason: null,
        allow_reset_speakers: false,
        project: {},
        end_time: moment().format()
      } as any;
    });

    setMediaContent(prevState => {
      const resynced = prevState.map(mc => {
        const upload = staleOptimisticUploads.find(content => content.contentId === mc.id);
        return upload && (mc as any).__isOptimistic ? { ...mc, batch_status: toBatchStatus(upload.contentState) } : mc;
      });
      return [...contentUpload, ...resynced];
    });
    // `mediaContent` is intentionally a dependency (not just `contentUploadList`): a list refetch can
    // replace `mediaContent` without yet containing an in-flight upload's real record (e.g. it isn't
    // on the current page), and this effect must re-run to re-inject the optimistic card so the user
    // keeps seeing their upload. Each inject/resync fires one extra pass, which exits early at the
    // guard above without calling setMediaContent (so no re-render loop). Do NOT break this into a
    // useRef snapshot to avoid the extra pass — that would also disable the re-injection above.
  }, [contentUploadList, mediaContent]);

  function fetchMediaContentList({
    organization,
    limit,
    selectedEvents = [],
    searchTerm,
    idsArray
  }: {
    organization: string;
    limit: number;
    selectedEvents?: string[];
    searchTerm?: string | undefined;
    idsArray?: string[];
  }): Promise<void> {
    return assetsMediaContentList({
      queryParams: {
        organization: organization,
        limit,
        search: searchTerm,
        ...(idsArray && { ids: idsArray.join(',') }),
        ...(selectedEvents.length && { projects: selectedEvents.join(',') })
      }
    })
      .then(data => {
        if (idsArray?.length && data.results?.length) {
          setMediaContent(mc =>
            mc.map(m => (idsArray.includes(m.id) ? (data.results?.find(d => idsArray.includes(d.id)) ?? m) : m))
          );
        } else {
          setMediaContent(data?.count && data?.results ? data.results : []);
        }
      })
      .catch(() => {
        setShowErrorState(true);
      });
  }

  return {
    fetchMediaContentList,
    mediaContent,
    showErrorState,
    setMediaContent
  };
}
